// src/pages/AdminBookings.jsx
import React, { useEffect, useMemo, useState } from "react";
import { auth } from "../firebase";
import {
  subscribeAllBookings,
  subscribeCategories,
  updateBookingStatus,
  deleteBooking,
  subscribeUsers,
  updateUserRole,
  subscribeRoleConfigs,
  upsertRoleConfig,
} from "../lib/servicesApi";

const AdminBookings = () => {
  const [activeTab, setActiveTab] = useState("bookings"); // 'bookings' | 'config'
  const [me, setMe] = useState(null);

  // Load current user doc
  useEffect(() => {
    const unsub = subscribeUsers((all) => {
      const uid = auth?.currentUser?.uid;
      const mine = all.find((u) => u.id === uid) || null;
      setMe(mine);
    }, console.error);
    return () => unsub && unsub();
  }, []);
  const isAdmin = (me?.role || "").toLowerCase() === "admin";

  // ------------------ Bookings tab ------------------
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsError, setBookingsError] = useState(null);

  const [categories, setCategories] = useState([]);
  const [catError, setCatError] = useState(null);

  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const unsubCats = subscribeCategories(
      (items) => setCategories(items || []),
      (err) => setCatError(err),
    );
    return () => unsubCats && unsubCats();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    setBookingsLoading(true);
    const unsub = subscribeAllBookings(
      (items) => {
        setBookings(items);
        setBookingsLoading(false);
      },
      (err) => {
        setBookingsError(err);
        setBookingsLoading(false);
      },
      { category: category === "all" ? undefined : category },
    );
    return () => unsub && unsub();
  }, [isAdmin, category]);

  const normalized = (v) => String(v ?? "").toLowerCase();
  const filteredBookings = useMemo(() => {
    const term = normalized(search);
    if (!term) return bookings;
    return bookings.filter((b) => {
      const id = normalized(b.id);
      const sName = normalized(b.serviceName);
      const cat = normalized(b.category);
      const addr = normalized(b.address);
      const fd = b.formData || {};
      const phone = normalized(fd.phone || fd.mobile || fd.contactPhone || "");
      const customer = normalized(
        fd.name || fd.fullName || fd.customerName || "",
      );
      return (
        id.includes(term) ||
        sName.includes(term) ||
        phone.includes(term) ||
        customer.includes(term) ||
        cat.includes(term) ||
        addr.includes(term)
      );
    });
  }, [bookings, search]);

  const handleStatus = async (bookingId, status) => {
    try {
      await updateBookingStatus(bookingId, status);
    } catch (e) {
      alert(`Failed to update status: ${e?.message || e}`);
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete booking ${b.id}? This cannot be undone.`))
      return;
    try {
      await deleteBooking(b.id, b.serviceId);
    } catch (e) {
      alert(`Failed to delete booking: ${e?.message || e}`);
    }
  };

  // ------------------ Config tab (admin-only) ------------------
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!isAdmin) return;
    const unsubRoles = subscribeRoleConfigs(setRoles, console.error);
    const unsubUsers = subscribeUsers(setUsers, console.error);
    return () => {
      unsubRoles && unsubRoles();
      unsubUsers && unsubUsers();
    };
  }, [isAdmin]);

  const knownRoles = useMemo(() => {
    const base = ["manager", "employee", "resident"];
    const cfg = roles.map((r) => r.roleId || r.id).filter(Boolean);
    return Array.from(new Set([...base, ...cfg]));
  }, [roles]);

  const [selectedRole, setSelectedRole] = useState("manager");
  const [permCreate, setPermCreate] = useState(false);
  const [permRead, setPermRead] = useState(true);
  const [permUpdate, setPermUpdate] = useState(false);
  const [permDelete, setPermDelete] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");

  const loadRoleToForm = (roleId) => {
    const r = roles.find((x) => (x.roleId || x.id) === roleId);
    setSelectedRole(roleId);
    if (!r) {
      setPermCreate(false);
      setPermRead(true);
      setPermUpdate(false);
      setPermDelete(false);
      return;
    }
    setPermCreate(!!r.create);
    setPermRead(!!r.read);
    setPermUpdate(!!r.update);
    setPermDelete(!!r.delete);
  };

  useEffect(() => {
    if (selectedRole) loadRoleToForm(selectedRole);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles]);

  const saveRoleConfig = async () => {
    try {
      const roleId = (newRoleName || selectedRole || "").trim().toLowerCase();
      if (!roleId) {
        alert("Please select or enter a role name.");
        return;
      }
      await upsertRoleConfig(roleId, {
        create: permCreate,
        read: permRead,
        update: permUpdate,
        delete: permDelete,
      });
      if (newRoleName) setNewRoleName("");
      alert("Role configuration saved.");
    } catch (e) {
      alert(`Failed to save role config: ${e?.message || e}`);
    }
  };

  const [userSearch, setUserSearch] = useState("");
  const filteredUsers = useMemo(() => {
    const t = (userSearch || "").toLowerCase();
    if (!t) return users;
    return users.filter((u) => {
      const id = (u.id || "").toLowerCase();
      const name = (u.name || u.displayName || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      return id.includes(t) || name.includes(t) || email.includes(t);
    });
  }, [users, userSearch]);

  const setUserRole = async (uid, role) => {
    try {
      await updateUserRole(uid, { role, updatedByUid: auth?.currentUser?.uid });
      alert("Role updated.");
    } catch (e) {
      alert(`Failed to update role: ${e?.message || e}`);
    }
  };

  if (!isAdmin) {
    return (
      <section className="py-5">
        <div className="container">
          <div className="alert alert-danger">403 — Admins only.</div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-4">
      <div className="container">
        {/* Tabs */}
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "bookings" ? "active" : ""}`}
              onClick={() => setActiveTab("bookings")}
            >
              Bookings
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "config" ? "active" : ""}`}
              onClick={() => setActiveTab("config")}
            >
              Configuration
            </button>
          </li>
        </ul>

        {activeTab === "bookings" && (
          <>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              {/* Category filter */}
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0">Category</label>
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: 200 }}
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="all">All</option>
                  {(categories || []).map((c) => (
                    <option key={c.id} value={c.key}>
                      {c.label || c.key}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search */}
              <div className="flex-grow-1">
                <input
                  className="form-control form-control-sm"
                  placeholder="Search by booking ID, service name, phone, customer name, category, address…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {bookingsError && (
              <div className="alert alert-danger">
                Bookings error: {bookingsError.message}
              </div>
            )}
            {catError && (
              <div className="alert alert-warning">
                Categories load issue: {catError.message}
              </div>
            )}

            {bookingsLoading ? (
              <div className="text-center my-4">
                <div className="spinner-border text-primary" role="status" />
                <div className="small text-muted mt-2">Loading bookings…</div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-striped align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Booking ID</th>
                      <th>Service</th>
                      <th>Category</th>
                      <th>Requested At</th>
                      <th>Customer</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th>Status</th>
                      <th style={{ width: 220 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map((b) => {
                      const time = b.requestedAt?.seconds
                        ? new Date(
                            b.requestedAt.seconds * 1000,
                          ).toLocaleString()
                        : "-";
                      const fd = b.formData || {};
                      const customer =
                        fd.name || fd.fullName || fd.customerName || "-";
                      const phone =
                        b.mobile ||
                        fd.mobile ||
                        fd.contactPhone ||
                        fd.phone ||
                        "-";
                      return (
                        <tr key={b.id}>
                          <td className="text-monospace">{b.id}</td>
                          <td>{b.serviceName}</td>
                          <td>{b.category || "-"}</td>
                          <td>{time}</td>
                          <td>{customer}</td>
                          <td>{phone}</td>
                          <td
                            style={{ maxWidth: 240 }}
                            className="text-truncate"
                            title={b.address || ""}
                          >
                            {b.address || "-"}
                          </td>
                          <td>
                            <span className="badge bg-secondary">
                              {b.status}
                            </span>
                          </td>
                          <td className="d-flex flex-wrap gap-1">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleStatus(b.id, "confirmed")}
                              title="Confirm"
                            >
                              Confirm
                            </button>
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleStatus(b.id, "completed")}
                              title="Complete"
                            >
                              Complete
                            </button>
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => handleStatus(b.id, "cancelled")}
                              title="Cancel"
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(b)}
                              title="Delete"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredBookings.length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-center text-muted py-4">
                          No bookings found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === "config" && (
          <>
            {/* Roles & Permissions */}
            <div className="card mb-4">
              <div className="card-header">Roles & Permissions (UI gating)</div>
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label">Select role</label>
                    <select
                      className="form-select"
                      value={selectedRole}
                      onChange={(e) => loadRoleToForm(e.target.value)}
                    >
                      {knownRoles.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Or add new role</label>
                    <input
                      className="form-control"
                      placeholder="e.g., supervisor"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="permCreate"
                        checked={permCreate}
                        onChange={(e) => setPermCreate(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="permCreate">
                        Create
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="permRead"
                        checked={permRead}
                        onChange={(e) => setPermRead(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="permRead">
                        Read
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="permUpdate"
                        checked={permUpdate}
                        onChange={(e) => setPermUpdate(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="permUpdate">
                        Update
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="permDelete"
                        checked={permDelete}
                        onChange={(e) => setPermDelete(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="permDelete">
                        Delete
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <button
                      className="btn btn-primary"
                      onClick={saveRoleConfig}
                    >
                      Save Role Config
                    </button>
                    <small className="text-muted ms-2">
                      This controls UI gating only; server permissions are
                      enforced by Firestore rules.
                    </small>
                  </div>
                </div>

                <div className="table-responsive mt-3">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Role</th>
                        <th>Create</th>
                        <th>Read</th>
                        <th>Update</th>
                        <th>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map((r) => (
                        <tr key={r.id}>
                          <td>{r.roleId || r.id}</td>
                          <td>{r.create ? "Yes" : "No"}</td>
                          <td>{r.read ? "Yes" : "No"}</td>
                          <td>{r.update ? "Yes" : "No"}</td>
                          <td>{r.delete ? "Yes" : "No"}</td>
                        </tr>
                      ))}
                      {roles.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-muted">
                            No role configs saved yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* User Role Assignment */}
            <div className="card">
              <div className="card-header">Assign User Roles</div>
              <div className="card-body">
                <div className="d-flex gap-2 mb-3">
                  <input
                    className="form-control"
                    placeholder="Search users by id, name, or email…"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                  />
                </div>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>User ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th style={{ width: 320 }}>Set Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr key={u.id}>
                          <td className="text-monospace">{u.id}</td>
                          <td>{u.name || u.displayName || "-"}</td>
                          <td>{u.email || "-"}</td>
                          <td>
                            <span className="badge bg-secondary">
                              {u.role || "-"}
                            </span>
                          </td>
                          <td className="d-flex gap-2">
                            <select
                              className="form-select form-select-sm"
                              defaultValue={u.role || "resident"}
                              onChange={(e) =>
                                setUserRole(u.id, e.target.value)
                              }
                            >
                              {knownRoles.map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                            {/* Optional: capture employee's serviceCategory (requires rules to allow admin to update this field) */}
                            {/* <input className="form-control form-control-sm" placeholder="serviceCategory for employee" /> */}
                          </td>
                        </tr>
                      ))}
                      {filteredUsers.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center text-muted py-4"
                          >
                            No users found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <small className="text-muted">
                  Note: Updating additional fields like{" "}
                  <code>serviceCategory</code> requires a rules change.
                </small>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default AdminBookings;
